import { useEffect, useState, type ChangeEvent } from 'react';

import { ArrowRight, ChevronDown, ChevronUp } from '@remarkable/ark-icons';
import {
  Input as ArkInput,
  ErrorMessage,
  Field,
  Label,
  Typography,
} from '@remarkable/ark-web';
import { Controller, useForm } from 'react-hook-form';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { useCart } from 'src/api/queries';
import { useCountry, useSetCountry } from 'src/api/queries';
import {
  Button,
  Form,
  Input,
  NotificationBox,
  SomethingWentWrong,
  Spinner,
  SplitHorizontalLayout,
} from 'src/components';
import { CountryPicker2 } from 'src/components/CountryPicker2';
import { InputTaxId } from 'src/components/InputTaxId';
import { StatePicker2 } from 'src/components/StatePicker2';
import { COUNTRIES } from 'src/utils/countryList';

import { Trans, useLingui } from '@lingui/react/macro';
import { useCheckoutForm } from '../checkoutFormStore';
import type { CheckoutFormState, CheckoutFormStep } from './Checkout.types';

export function CheckoutDetailsForm({
  cart,
}: {
  cart: ReturnType<typeof useCart>;
}) {
  const { t } = useLingui();
  const [checkoutFormState, setCheckoutFormState] = useCheckoutForm();
  const country = useCountry();
  const setCountry = useSetCountry();
  const form = useForm<Partial<CheckoutFormState>>({
    defaultValues: { ...checkoutFormState },
  });
  const { watch } = form;
  const countryFieldValue = watch('country');
  const isUnitedStates = countryFieldValue === 'US';
  const isCanada = countryFieldValue === 'CA';
  const [showBillingInfo, setShowBillingInfo] = useState(false);

  useEffect(() => {
    // set default value for country in the form
    if (!country.isLoading && !form.getValues('country')) {
      form.setValue('country', country.data?.value);
    }
  }, [country.isLoading]);

  if (country.isLoading) {
    return <Spinner />;
  }
  if (cart.details.isError || cart.setup.isError || cart.country.isError) {
    return (
      <SomethingWentWrong
        error={cart.details.error || cart.setup.error || cart.country.error}
      />
    );
  }
  return (
    <div className="flex flex-col gap-16">
      <Form
        {...form}
        onSubmit={(data) => {
          const newState = {
            ...data,
            currentStep: 'PAYMENT' as CheckoutFormStep,
          };
          cart.checkout.mutate(
            {
              billing_address: {
                first_name: newState.firstName ?? '',
                last_name: newState.lastName ?? '',
                // Company name is optional
                company_name: newState.companyName || undefined,
                line_1: newState.billingAddress1 ?? '',
                line_2: newState.billingAddress2 ?? '',
                city: newState.city ?? '',
                county: newState.state || newState.province || '',
                postcode: newState.postalCode ?? '',
                country: newState.country ?? '',
                VAT: newState.VAT,
              },
            },
            {
              onSuccess: () => {
                setCheckoutFormState(newState);
              },
            }
          );
        }}
      >
        <div className="flex flex-col gap-16">
          <SplitHorizontalLayout>
            <Input
              name="firstName"
              label={t`First name`}
              autoComplete="given-name"
              required
            />
            <Input
              name="lastName"
              label={t`Last name`}
              autoComplete="family-name"
              required
            />
          </SplitHorizontalLayout>

          <Controller
            control={form.control}
            name="country"
            rules={{
              required: true,
              onChange(event: ChangeEvent<HTMLSelectElement>) {
                const newCountry = COUNTRIES.find(
                  (c) => c.value === event.target.value
                );

                if (!newCountry) {
                  throw new Error('Country does not exist!');
                }

                void setCountry(newCountry);
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <Field className="w-full">
                  <Label htmlFor={field.name}>
                    <Trans>Country</Trans>
                  </Label>

                  <CountryPicker2
                    id={field.name}
                    hasError={!!fieldState.error}
                    aria-invalid={!!fieldState.error}
                    aria-errormessage={fieldState.error?.message}
                    {...field}
                  />

                  {fieldState.error?.message && (
                    <ErrorMessage data-cy="country-error-message">
                      {fieldState.error.message}
                    </ErrorMessage>
                  )}
                </Field>
              );
            }}
          />

          <SplitHorizontalLayout>
            {isUnitedStates && (
              <>
                <Controller
                  control={form.control}
                  name="state"
                  rules={{
                    deps: ['country', 'postalCode'], // Will trigger validation on postalCode that in turn updates the tax
                    required: t`State is required`,
                  }}
                  shouldUnregister
                  render={({ field, fieldState }) => {
                    return (
                      <Field className="w-full flex-1">
                        <Label htmlFor={field.name}>
                          <Trans context="Address level">State</Trans>
                        </Label>

                        <StatePicker2
                          country={'US'}
                          id={field.name}
                          hasError={!!fieldState.error}
                          aria-invalid={!!fieldState.error}
                          aria-errormessage={fieldState.error?.message}
                          {...field}
                        />

                        {fieldState.error?.message && (
                          <ErrorMessage data-cy="state-error-message">
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </Field>
                    );
                  }}
                />
                <Controller
                  control={form.control}
                  name="postalCode"
                  rules={{
                    required: t`Zip code is required`,
                    deps: ['country', 'state'],
                    validate: {
                      length: (value: string | undefined) => {
                        if (!value) return t`ZIP code must be provided`;
                        const zipPattern = /^\d{5}([-]\d{4})?$/i;
                        return (
                          zipPattern.test(value) ||
                          t`Please enter a valid zip code for the United States, for example 90210 or 90210-1234.`
                        );
                      },
                      tax: async (value, form) => {
                        if (!value || !form.state || !form.country)
                          return false;

                        const result = await cart.updateTax
                          .mutateAsync({
                            country: form.country,
                            county: form.state,
                            zip: value,
                            city: form.city,
                          })
                          .then(() => true)
                          .catch(() => t`Invalid zip code`);

                        return result;
                      },
                    },
                  }}
                  shouldUnregister
                  render={({ field, fieldState }) => (
                    <Field className="w-full min-w-0 flex-1">
                      <Label htmlFor={field.name}>
                        <Trans>Zip code</Trans>
                      </Label>
                      <ArkInput
                        data-cy="postal-code-input"
                        id={field.name}
                        inputMode="numeric"
                        {...field}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                      />

                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="postal-code-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  )}
                />
              </>
            )}

            {isCanada && (
              <Controller
                control={form.control}
                name="province"
                rules={{
                  deps: ['country', 'postalCode'], // Will trigger validation on postalCode that in turn updates the tax
                  required: t`Province is required`,
                }}
                shouldUnregister
                render={({ field, fieldState }) => {
                  const provinceName = field.value;
                  return (
                    <Field className="w-full">
                      <Label htmlFor={field.name}>
                        <Trans context="Canadian province">
                          Province {provinceName}
                        </Trans>
                      </Label>

                      <StatePicker2
                        country={'CA'}
                        id={field.name}
                        hasError={!!fieldState.error}
                        aria-invalid={!!fieldState.error}
                        aria-errormessage={fieldState.error?.message}
                        {...field}
                      />

                      {fieldState.error?.message && (
                        <ErrorMessage data-cy="state-error-message">
                          {fieldState.error.message}
                        </ErrorMessage>
                      )}
                    </Field>
                  );
                }}
              />
            )}
          </SplitHorizontalLayout>

          <button
            data-cy="billing-info-optional"
            aria-expanded={showBillingInfo}
            className="relative my-16 h-32 w-full cursor-pointer border-none bg-none text-left body-lg"
            onClick={(e) => {
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
                  text: e.currentTarget.innerText,
                  action: 'toggle billing info in checkout',
                })
              );
              setShowBillingInfo(!showBillingInfo);
            }}
            type="button"
          >
            <Typography
              variant="interface-lg"
              fontWeight="semibold"
              leadingNone
            >
              <Trans>
                Billing information{' '}
                <span className="font-regular">(optional)</span>
              </Trans>
            </Typography>
            {!showBillingInfo ? (
              <ChevronDown
                size={24}
                className="absolute top-0 right-12 text-default"
              />
            ) : (
              <ChevronUp
                size={24}
                className="absolute top-0 right-12 text-default"
              />
            )}
          </button>

          {showBillingInfo && (
            <>
              <Input
                data-cy="billing-address1"
                label={t`Billing address 1`}
                autoComplete="street-address"
                name="billingAddress1"
              />
              <Input
                data-cy="billing-address2"
                name="billingAddress2"
                label={t`Billing address 2`}
              />

              <SplitHorizontalLayout>
                {!isUnitedStates && (
                  <Input
                    data-cy="billing-post-code"
                    name="postalCode"
                    label={t`Postal code`}
                    inputMode="numeric"
                    autoComplete="postal-code"
                  />
                )}
                <Input
                  data-cy="billing-city"
                  name="city"
                  label={t`City`}
                  autoComplete="address-level2"
                />
              </SplitHorizontalLayout>

              <Input
                data-cy="billing-company-name"
                name="companyName"
                label={t`Company name`}
                autoComplete="organization"
              />
              <InputTaxId className="my-0" country={countryFieldValue} />
            </>
          )}

          <Button
            variant="primary"
            type="submit"
            className="w-full ll:w-fit"
            data-cy="continue-button"
            loading={
              form.formState.isSubmitting ||
              cart.updateTax.isPending ||
              cart.checkout.isPending ||
              cart.setup.isLoading
            }
            disabled={
              form.formState.isSubmitting ||
              cart.checkout.isPending ||
              cart.setup.isLoading
            }
            analytics={{
              text: t`Continue`,
              location: ComponentLocations.CHECKOUT.CONTINUE,
              action: 'continue to payment',
            }}
          >
            <span>
              <Trans>Continue</Trans>
            </span>
            <ArrowRight size={24} />
          </Button>
        </div>
      </Form>
      <NotificationBox
        data-cy="checkout-details-error-box"
        error={cart.checkout.error}
      />
    </div>
  );
}
